import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { makeStyles } from "tss-react/mui";
import { hourPixelHeight, timePixelWidth, weekdayPixelHeight } from "./Scheduler";
import Weekday, {
    LabeledWeekday,
    getLabeledWeekdays,
    getShiftedWeekday,
    getShortLabeledWeekdayForWeekdayIndex,
    getWeekdayFromIndex,
    getWeekdayIndexShift,
    isWeekdayCurrentDay,
} from "../../../utils/weekday";
import { isExcludedDateWithReason } from "../../../utils/date";
import {
    getTimeSpanDecimalHours,
    getTimeSpanDifferenceDecimalHours,
    getTimeSpanFromDecimal,
    getTimeSpanFromString,
    increaseTimeSpanCappedAt24,
    timeShiftWithOverflow,
} from "../../../utils/time-span";
import Point from "../../../interfaces/robot/rooms/point";
import { getNewScheduleEntry } from "../../../interfaces/robot/scheduling/schedule-entry";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import { selectSelectedRoom } from "../../../store/room/room-selectors";
import { 
    selectScheduleEntries,
    selectSchedulerMouseState,
    selectSelectedSchedule,
 } from "../../../store/room/scheduler/scheduler-selectors";
import SchedulerControllerMouseState from "../../../enums/robot/rooms/scheduler-controller-mouse-state";
import HttpFetchStatus from "../../../enums/api/http-fetch-status";
import { NavigatorAvailability, NavigatorName, NavigatorType } from "../../../enums/command-hub/navigator";
import { selectExcludedDates } from "../../../store/robot-configurations/robot-configurations-selectors";

const schedulerHours = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];

const useStyles = makeStyles()((theme) => ({
    table: {
        tableLayout: "fixed",
    },
    timeCell: {
        position: "relative",
        borderBottom: "none",
        borderRight: "1px solid",
        borderColor: theme.palette.grey[300],
        width: timePixelWidth,
        height: hourPixelHeight,
        "&:after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            right: 0,
            width: theme.spacing(1),
            height: 1,
            background: theme.palette.grey[300],
        },
    },
    headerCell: {
        position: "sticky",
        textAlign: "center",
        borderRight: "none",
        height: weekdayPixelHeight,
        padding: 0,
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(1),
    },
    dayCell: {
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
        backgroundColor: "white",
        "&:after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            height: theme.spacing(2.5),
            width: 1,
            background: theme.palette.grey[300],
        },
    },
    cornerCell: {
        position: "sticky",
        textAlign: "center",
        backgroundColor: "white",
        width: theme.spacing(6),
        "&:after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            right: 0,
            height: theme.spacing(2.5),
            width: 1,
        },
        "&:before": {
            content: '""',
            position: "absolute",
            bottom: 0,
            right: 0,
            width: theme.spacing(2.5),
            height: 1,
            background: theme.palette.grey[300],
        },
    },
    bottomCell: {
        borderBottom: "none",
        "&:after": {
            background: "none",
        },
    },
    tableHeaderCurrentDayMarker: {
        width: "100%",
        height: "2px",
        position: "absolute",
        left: "0",
        bottom: "0",
        backgroundColor: theme.palette.primary.main,
    },
    tableCellGrey: {
        backgroundColor: theme.palette.grey[100],
    },
}));

export const getCursorPointFromEvent = (event: MouseEvent | TouchEvent) => {
    let cursorPoint: Point = { coordinateX: 0, coordinateY: 0 };
    if (event instanceof MouseEvent) {
        cursorPoint.coordinateX = event.clientX;
        cursorPoint.coordinateY = event.clientY;
    } else if (event instanceof TouchEvent) {
        cursorPoint.coordinateX = event.changedTouches[0].clientX;
        cursorPoint.coordinateY = event.changedTouches[0].clientY;
    }
    return cursorPoint;
};

const labeledWeekdays = getLabeledWeekdays();

const SchedulerTimeTable: React.FC<{
    tableContainerRef: React.RefObject<HTMLDivElement>;
    floorId: string;
}> = ({ tableContainerRef, floorId }) => {
    const { cx, classes } = useStyles();
    const { t } = useTranslation();

    const dispatch = useSharedDispatch();

    const { setSelectedScheduleEntry, addNewDraftScheduleEntry, updateDraftScheduleEntry, getExcludedDates } =
      useSharedActions();

    const roomData = useSharedSelector((state) => selectSelectedRoom(state));
    const selectedScheduleEntry = useSharedSelector((state) => selectSelectedSchedule(state));
    const scheduleEntries = useSharedSelector((state) => selectScheduleEntries(state)).data;
    const schedulerMouseState = useSharedSelector((state) => selectSchedulerMouseState(state));
    const excludedDates = useSharedSelector((state) => selectExcludedDates(state));
    
    const defaultNavigator = {
        name: NavigatorName.ZoneCleaning,
        type: NavigatorType.CleaningNavigator,
        availability: NavigatorAvailability.Public,
        hideInList: true,
        isWallFollowConfigurable: false,
        isRepeatConfigurable: true,
    };

    useEffect(() => {
      if (excludedDates.rest.getStatus === HttpFetchStatus.Idle) {
        dispatch(getExcludedDates());
      }
    }, [dispatch, getExcludedDates, excludedDates.rest.getStatus]);

    const tableCellDoubleClickHandler = (weekday: Weekday, hour: number) => {
        if (roomData?.secondaryStation != null) {
            return;
        }
        const timeFrom = getTimeSpanFromDecimal(hour);
        if (timeFrom != null && defaultNavigator != null) {
            const scheduleEntry = getNewScheduleEntry(
                weekday,
                weekday,
                timeFrom,
                increaseTimeSpanCappedAt24(timeFrom, { hours: 1, minutes: 0 }),
                true
            );
            dispatch(
                addNewDraftScheduleEntry({scheduleEntries: [scheduleEntry], isTemporary: true})
            );
        }
    };

    const getTimeAndDayFromMousePosition = useCallback((event: MouseEvent | TouchEvent) => {
        const cursorPoint = getCursorPointFromEvent(event);
        const tableCellElement = document
            .elementsFromPoint(cursorPoint.coordinateX, cursorPoint.coordinateY)
            .find((e) => e.tagName === "TD");

        if (tableCellElement?.id.split("_").length !== 2) {
            return;
        }

        const day = getWeekdayFromIndex(parseInt(tableCellElement?.id.split("_")[0], 10));
        if (day === undefined) {
            return;
        }

        return {
            weekday: day,
            time: getTimeSpanFromString(tableCellElement?.id.split("_")[1]),
        };
    }, []);

    const mouseTouchMoveHandler = useCallback(
      (event: MouseEvent | TouchEvent) => {
        const cursorDayTime = getTimeAndDayFromMousePosition(event);
        if (cursorDayTime === undefined) {
          return;
        }
        if (selectedScheduleEntry.selectedEntry === null) {
          return;
        }
        if (
          schedulerMouseState === SchedulerControllerMouseState.DragTopOfSchedule &&
          cursorDayTime.time != null &&
          !scheduleEntries.some(
            (s) =>
              s.scheduleEntry.id !== selectedScheduleEntry.selectedEntry!.id &&
              s.scheduleEntry.weekdayTo === selectedScheduleEntry.selectedEntry!.weekdayFrom &&
              getTimeSpanDecimalHours(s.scheduleEntry.timeFrom) <
                getTimeSpanDecimalHours(selectedScheduleEntry.selectedEntry!.timeFrom) &&
              getTimeSpanDecimalHours(s.scheduleEntry.timeTo) > getTimeSpanDecimalHours(cursorDayTime.time)
          )
        ) {
          dispatch(
            updateDraftScheduleEntry({
              schedule: {
                id: selectedScheduleEntry.selectedEntry.id,
                timeFrom: cursorDayTime.time,
              },
            })
          );
        } else if (schedulerMouseState === SchedulerControllerMouseState.DragBottomOfSchedule) {
          const newTimeTo = increaseTimeSpanCappedAt24(cursorDayTime.time, { hours: 0, minutes: 30 });
          if (
            newTimeTo != null &&
            !scheduleEntries.some(
              (s) =>
                s.scheduleEntry.id !== selectedScheduleEntry.selectedEntry!.id &&
                s.scheduleEntry.weekdayFrom === selectedScheduleEntry.selectedEntry!.weekdayTo &&
                getTimeSpanDecimalHours(s.scheduleEntry.timeFrom) >
                  getTimeSpanDecimalHours(selectedScheduleEntry.selectedEntry!.timeFrom) &&
                getTimeSpanDecimalHours(s.scheduleEntry.timeFrom) < getTimeSpanDecimalHours(newTimeTo)
            )
          ) {
            dispatch(
              updateDraftScheduleEntry({
                schedule: {
                  id: selectedScheduleEntry.selectedEntry.id,
                  timeTo: newTimeTo,
                },
              })
            );
          }
        } else if (
          schedulerMouseState === SchedulerControllerMouseState.DragSchedule &&
          selectedScheduleEntry.initialDay === null
        ) {
          dispatch(
            setSelectedScheduleEntry({
              entry: selectedScheduleEntry.selectedEntry,
              time: cursorDayTime.time,
              day: cursorDayTime.weekday,
              mouseState: schedulerMouseState,
            })
          );
        } else if (
          schedulerMouseState === SchedulerControllerMouseState.DragSchedule &&
          selectedScheduleEntry.initialDay != null &&
          selectedScheduleEntry.initialTime != null
        ) {
          if (tableContainerRef.current != null) {
            tableContainerRef.current.style.overflow = "hidden";
          }
          const timeDragDifference = getTimeSpanDifferenceDecimalHours(
            selectedScheduleEntry.initialTime,
            cursorDayTime.time
          );
          const newTimeFromWithShift = timeShiftWithOverflow(
            selectedScheduleEntry.selectedEntry.timeFrom,
            timeDragDifference
          );
          const newTimeToWithShift = timeShiftWithOverflow(
            selectedScheduleEntry.selectedEntry.timeTo,
            timeDragDifference
          );
          const weekdayIndexShift = getWeekdayIndexShift(cursorDayTime.weekday, selectedScheduleEntry.initialDay);
          const newWeekdayTo = getShiftedWeekday(
            selectedScheduleEntry.selectedEntry.weekdayTo,
            weekdayIndexShift + newTimeToWithShift.dayShift
          );
          const newWeekdayFrom = getShiftedWeekday(
            selectedScheduleEntry.selectedEntry.weekdayFrom,
            weekdayIndexShift + newTimeFromWithShift.dayShift
          );
          dispatch(
            updateDraftScheduleEntry({
              schedule: {
                id: selectedScheduleEntry.selectedEntry.id,
                weekdayFrom: newWeekdayFrom,
                weekdayTo: newWeekdayTo,
                timeFrom: newTimeFromWithShift.timespan!,
                timeTo: newTimeToWithShift.timespan!,
              },
            })
          );
        }
      },
      [
        dispatch,
        getTimeAndDayFromMousePosition,
        setSelectedScheduleEntry,
        updateDraftScheduleEntry,
        scheduleEntries,
        schedulerMouseState,
        selectedScheduleEntry.initialDay,
        selectedScheduleEntry.initialTime,
        selectedScheduleEntry.selectedEntry,
        tableContainerRef,
      ]
    );

    useEffect(() => {
        if (schedulerMouseState === SchedulerControllerMouseState.Default && tableContainerRef.current != null) {
            tableContainerRef.current.style.overflow = "auto";
        }
    }, [schedulerMouseState, tableContainerRef]);

    useEffect(() => {
        const tableContainer = tableContainerRef.current;
        if (tableContainer != null) {
            tableContainer.addEventListener("touchmove", mouseTouchMoveHandler, { passive: false });
            tableContainer.addEventListener("mousemove", mouseTouchMoveHandler);
        }

        return () => {
            if (tableContainer != null) {
                tableContainer.removeEventListener("mousemove", mouseTouchMoveHandler);
                tableContainer.removeEventListener("touchmove", mouseTouchMoveHandler);
            }
        };
    }, [mouseTouchMoveHandler, tableContainerRef]);

    return (
      <Table stickyHeader className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.cornerCell}></TableCell>
            {labeledWeekdays.map((weekday) => {
              const noGoDateReason = isExcludedDateWithReason(weekday.index, excludedDates.data, roomData?.id);
              return (
                <Tooltip
                  title={(t("scheduler-excludedDate") as string) + noGoDateReason}
                  disableHoverListener={noGoDateReason == null}
                  key={weekday.index}
                >
                  <TableCell
                    className={cx(classes.headerCell, classes.dayCell, {
                      [classes.tableCellGrey]: noGoDateReason != null,
                    })}
                  >
                    <Typography
                      sx={(theme) => ({
                        textAlign: "center",
                        color: isWeekdayCurrentDay(weekday.index) ? theme.palette.primary.main : "inherit",
                        fontWeight: isWeekdayCurrentDay(weekday.index) ? theme.typography.button : undefined,
                      })}
                      variant="button"
                    >
                      {getShortLabeledWeekdayForWeekdayIndex(weekday.index)}
                    </Typography>
                    {isWeekdayCurrentDay(weekday.index) && <div className={classes.tableHeaderCurrentDayMarker}></div>}
                  </TableCell>
                </Tooltip>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {schedulerHours.map((hour) => (
            <React.Fragment key={hour}>
              <TableRow sx={{ height: "12px", overflow: "hidden" }}>
                <TableCell className={cx(classes.timeCell, { [classes.bottomCell]: hour === 23 })} rowSpan={2}>
                  {hour !== 0 && (
                    <Typography variant="caption" sx={{ position: "absolute", top: -9, right: 16 }}>
                      {hour}
                    </Typography>
                  )}
                </TableCell>
                {labeledWeekdays.map((weekday, index) => (
                  <TimeCell key={index} weekday={weekday} hour={hour} onDoubleClick={tableCellDoubleClickHandler} />
                ))}
              </TableRow>
              <TableRow>
                {labeledWeekdays.map((weekday, index) => (
                  <TimeCell
                    key={index}
                    weekday={weekday}
                    hour={hour}
                    halfHour
                    onDoubleClick={tableCellDoubleClickHandler}
                  />
                ))}
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    );
};

export default SchedulerTimeTable;

const TimeCell: React.FC<{
    weekday: LabeledWeekday;
    hour: number;
    halfHour?: boolean;
    onDoubleClick: (weekday: Weekday, hour: number) => void;
}> = ({ weekday, hour, halfHour = false, onDoubleClick }) => {
    const { cx, classes } = useStyles();

    // handle double click with touch screen
    const lastTap = useRef<number | null>(null);
    const handleTouch = () => {
      const now = Date.now();
      if (lastTap.current && now - lastTap.current < 300) {
        onDoubleClick(weekday.value, halfHour ? hour + 0.5 : hour);
      }
      lastTap.current = now;
    };

    return (
        <TableCell
            id={`${weekday.index}_${hour}:${halfHour ? "30" : "00"}`}
            sx={(theme) => ({
                borderLeft: weekday.index !== 1 ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                height: `${parseInt(hourPixelHeight, 10) / 2}px`,
                padding: 0,
                borderBottom: `1px solid ${theme.palette.grey[300]}`,
            })}
            className={cx({
                [classes.bottomCell]: hour === 23 || !halfHour,
                [classes.tableCellGrey]: hour < 8 || hour >= 18,
            })}
            onDoubleClick={() => {
                onDoubleClick(weekday.value, halfHour ? hour + 0.5 : hour);
            }}
            onTouchEnd={handleTouch}
        ></TableCell>
    );
};